import {
  CheckBadgeIcon,
  KeyIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { Checkbox, Radio, Spinner, Typography } from "@material-tailwind/react";
import { LocationFormProps } from "data/reduxInterfaces";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { FC, Fragment, HTMLAttributes, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { fetchRules } from "redux/features/Admin/rules/rulesSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";

import { z } from "zod";

interface Rules {
  id: number;
  designation: string;
}

const formSchema = z.object({
  description: z.string().min(10, { message: "La description est obligatoire (minimum 10 caractères)" }),
  handicap: z.boolean({ required_error: "L'accessibilité aux personnes à mobilité réduite est obligatoire" }),
  parking: z.string().min(1, { message: "L'accès au stationnement est obligatoire" }),
  wc: z.string().min(1, { message: "L'accès aux toilettes est obligatoire" }),
  water: z.boolean({ required_error: "L'accès au point d'eau est obligatoire" }),
  light: z.boolean({ required_error: "L'éclairage extérieur est obligatoire" }),
  rules: z.array(z.string()).optional(),
  condition: z.array(z.string()).optional(),
});

const LocationAccessibilityV2: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  isLoading,
  handleForm,
  data,
  FormData,
}) => {
  const dispatch = useAppDispatch();

  const rulesState = useAppSelector((state) => state.featureRules);

  const [dataLoading, setDataLoading] = useState(true);
  const [tag, setTag] = useState<string>("");

  const {
    handleSubmit,
    register,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      description: data?.description ?? "",
      handicap: data?.handicap ?? false,
      parking: data?.parking ?? "libre",
      wc: data?.wc ?? "intérieur",
      water: data?.water ?? false,
      light: data?.light ?? false,
      rules: data?.rules ?? [],
      condition: data?.condition ?? [],
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    dispatch(fetchRules());
  }, []);

  useEffect(() => {
    if (data) {
      setValue("description", data.description);
      setValue("handicap", data.handicap);
      setValue("parking", data.parking);
      setValue("wc", data.wc);
      setValue("water", data.water);
      setValue("light", data.light);
      setValue("rules", data.rules);
      setValue("condition", data.condition);
    }
  }, [data]);

  useEffect(() => {
    if (rulesState.rules) {
      setDataLoading(false);
    }
  }, [rulesState.rules]);

  useEffect(() => {
    if (handleForm.type === "chekLocationFormFive") {
      // Utiliser la soumission normale React Hook Form
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  const handleSelect = (ruleDesignation: string, isChecked: boolean) => {
    const updatedselectedRules = isChecked
      ? [...(getValues("rules") ?? []), ruleDesignation]
      : getValues("rules")?.filter((rule: string) => rule !== ruleDesignation);

    setValue("rules", updatedselectedRules as string[]);
    trigger("rules");
  };

  const handleAddTag = () => {
    if (tag.trim() !== "") {
      const dd = getValues("condition") || [];
      setValue("condition", [...dd, tag.trim()]);
      trigger("condition");
      setTag("");
    }
  };

  const handleRemoveTag = (index: number) => {
    const newTags = [...(getValues("condition") || [])];
    newTags.splice(index, 1);
    setValue("condition", newTags);
    trigger("condition");
  };

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    FormData(data);
  };

  return (
    <Fragment>
      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="w-full">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Description et Accessibilité</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Complétez votre description et définissez les règles d'accessibilité
            </p>
            <div className="border-b-2 w-20 border-eventToBee mt-4"></div>
          </div>

          {/* Section Description */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <div className="flex items-center mb-6">
              <svg className="w-6 h-6 text-eventToBee mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Description de votre espace</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Décrivez votre espace *
                </label>
                <textarea
                  {...register("description")}
                  placeholder="Présentez votre espace aux visiteurs. Rédigez un bref résumé en mettant en avant les caractéristiques de votre espace."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-eventToBee focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.description.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Section Accessibilité */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
            <div className="flex items-center mb-6">
              <UserCircleIcon className="w-6 h-6 text-eventToBee mr-3" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Accessibilité</h3>
            </div>

            <div className="grid grid-cols-1 gap-8">
              {/* Mobilité réduite */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Accès aux personnes à mobilité réduite *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div
                    className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      getValues("handicap") === true
                        ? "border-eventToBee bg-blue-50 dark:bg-blue-900/20"
                        : "border-gray-200 dark:border-gray-600"
                    }`}
                    onClick={() => {
                      setValue("handicap", true);
                      trigger("handicap");
                    }}
                  >
                    <Radio
                      color="blue"
                      name="handicap"
                      checked={getValues("handicap") === true}
                      readOnly
                      disabled={isLoading}
                    />
                    <Typography className="text-gray-700 dark:text-gray-300">Oui, accessible</Typography>
                  </div>
                  <div
                    className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      getValues("handicap") === false
                        ? "border-eventToBee bg-blue-50 dark:bg-blue-900/20"
                        : "border-gray-200 dark:border-gray-600"
                    }`}
                    onClick={() => {
                      setValue("handicap", false);
                      trigger("handicap");
                    }}
                  >
                    <Radio
                      color="blue"
                      name="handicap"
                      checked={getValues("handicap") === false}
                      readOnly
                      disabled={isLoading}
                    />
                    <Typography className="text-gray-700 dark:text-gray-300">Non accessible</Typography>
                  </div>
                </div>
                {errors.handicap?.message && (
                  <div className="text-xs text-red-500 mt-1">{errors.handicap.message}</div>
                )}
              </div>

              {/* Stationnement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Accès au stationnement *
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {["libre", "privé", "extérieur"].map((option) => (
                    <div
                      key={option}
                      className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        getValues("parking") === option
                          ? "border-eventToBee bg-blue-50 dark:bg-blue-900/20"
                          : "border-gray-200 dark:border-gray-600"
                      }`}
                      onClick={() => {
                        setValue("parking", option);
                        trigger("parking");
                      }}
                    >
                      <Radio
                        color="blue"
                        name="parking"
                        checked={getValues("parking") === option}
                        readOnly
                        disabled={isLoading}
                      />
                      <Typography className="text-gray-700 dark:text-gray-300 capitalize">{option}</Typography>
                    </div>
                  ))}
                </div>
                {errors.parking?.message && (
                  <div className="text-xs text-red-500 mt-1">{errors.parking.message}</div>
                )}
              </div>

              {/* Toilettes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Accès aux toilettes *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {["intérieur", "extérieur"].map((option) => (
                    <div
                      key={option}
                      className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        getValues("wc") === option
                          ? "border-eventToBee bg-blue-50 dark:bg-blue-900/20"
                          : "border-gray-200 dark:border-gray-600"
                      }`}
                      onClick={() => {
                        setValue("wc", option);
                        trigger("wc");
                      }}
                    >
                      <Radio
                        color="blue"
                        name="wc"
                        checked={getValues("wc") === option}
                        readOnly
                        disabled={isLoading}
                      />
                      <Typography className="text-gray-700 dark:text-gray-300 capitalize">{option}</Typography>
                    </div>
                  ))}
                </div>
                {errors.wc?.message && <div className="text-xs text-red-500 mt-1">{errors.wc.message}</div>}
              </div>

              {/* Point d'eau et Éclairage */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Accès au point d'eau *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[true, false].map((value) => (
                      <div
                        key={value.toString()}
                        className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          getValues("water") === value
                            ? "border-eventToBee bg-blue-50 dark:bg-blue-900/20"
                            : "border-gray-200 dark:border-gray-600"
                        }`}
                        onClick={() => {
                          setValue("water", value);
                          trigger("water");
                        }}
                      >
                        <Radio
                          color="blue"
                          name="water"
                          checked={getValues("water") === value}
                          readOnly
                          disabled={isLoading}
                        />
                        <Typography className="text-gray-700 dark:text-gray-300">
                          {value ? "Oui" : "Non"}
                        </Typography>
                      </div>
                    ))}
                  </div>
                  {errors.water?.message && (
                    <div className="text-xs text-red-500 mt-1">{errors.water.message}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Éclairage extérieur *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {[true, false].map((value) => (
                      <div
                        key={value.toString()}
                        className={`flex items-center space-x-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          getValues("light") === value
                            ? "border-eventToBee bg-blue-50 dark:bg-blue-900/20"
                            : "border-gray-200 dark:border-gray-600"
                        }`}
                        onClick={() => {
                          setValue("light", value);
                          trigger("light");
                        }}
                      >
                        <Radio
                          color="blue"
                          name="light"
                          checked={getValues("light") === value}
                          readOnly
                          disabled={isLoading}
                        />
                        <Typography className="text-gray-700 dark:text-gray-300">
                          {value ? "Oui" : "Non"}
                        </Typography>
                      </div>
                    ))}
                  </div>
                  {errors.light?.message && (
                    <div className="text-xs text-red-500 mt-1">{errors.light.message}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section Règles */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center mb-6">
              <ShieldCheckIcon className="w-6 h-6 text-eventToBee mr-3" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Règles et conditions d'utilisation
              </h3>
            </div>

            {/* Règles prédéfinies */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                Règles communes (optionnel)
              </label>
              {dataLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner color="blue" className="w-8 h-8" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {rulesState.rules?.map((rule: Rules) => (
                    <div
                      key={rule.designation}
                      className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <Checkbox
                        checked={!!getValues("rules")?.includes(rule.designation)}
                        onChange={(e) => handleSelect(rule.designation, e.target.checked)}
                        className="text-eventToBee"
                      />
                      <Typography className="text-sm text-gray-700 dark:text-gray-300">
                        {rule.designation}
                      </Typography>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Règles personnalisées */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                Règles supplémentaires (optionnel)
              </label>

              {/* Règles ajoutées */}
              {getValues("condition") && getValues("condition")!.length > 0 && (
                <div className="mb-4 space-y-2">
                  {getValues("condition")?.map((tagItem: string, index: number) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border"
                    >
                      <span className="text-sm text-gray-700 dark:text-gray-300">{tagItem}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <i className="las la-times text-lg"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Ajout de nouvelles règles */}
              <div className="flex gap-3">
                <Input
                  className="flex-1"
                  placeholder="Ex: Interdiction de fumer, Pas d'animaux..."
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <ButtonPrimary onClick={handleAddTag} type="button" className="px-6">
                  Ajouter
                </ButtonPrimary>
              </div>
            </div>
          </div>
        </div>
      </form>
    </Fragment>
  );
};

export default LocationAccessibilityV2;
